<template>
    <p-modal
        name="cashierReconciliationModal"
        label="POS Device by Cashier(s) Reconciliation"
        ref="cashierReconciliation"
        size="lg"
        @ok.prevent="handleModalOk"
    >
        <template v-slot:default>
            <p-form ref="cashierReconciliationForm">
                <div class="container-fluid content-wrapper">
                    <b-row>
                        <b-col>
                            <p>Sales subtotaled by cashier or POS device</p>
                        </b-col>
                    </b-row>
                    <b-row cols="1" cols-lg="2">
                        <b-col>
                            <p-card title="Cashier(s)">
                                <p-filter-select
                                    data-type="nbcUsers"
                                    rules="required"
                                    :default-selected="true"
                                    :initial-display-count="99999"
                                    v-model="reportParameters.nbcUserIds"
                                />
                            </p-card>
                        </b-col>
                        <b-col>
                            <p-card title="Date Range">
                                <p-date-range-widget :date-range-options="reportParameters" />
                            </p-card>
                            <p-card title="Subtotal By">
                                <b-row>
                                    <b-col>
                                        <b-form-radio
                                            :value="false"
                                            v-model="reportParameters.subtotalByPosDevice">
                                            Cashier
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                                <b-row class="pt-1">
                                    <b-col>
                                        <b-form-radio
                                            :value="true"
                                            v-model="reportParameters.subtotalByPosDevice">
                                            POS Device
                                        </b-form-radio>
                                    </b-col>
                                </b-row>
                            </p-card>
                        </b-col>
                    </b-row>
                    <UserReportTemplate 
                        :user-report-template-options="userReportTemplateOptions"
                        v-model:selected-user-report-template-id="selectedUserReportTemplateId"
                        :selected-user-report-template="selectedUserReportTemplate"
                        v-model:template-name="templateName"
                        @selected-object="applyUserReportTemplate($event)" />
                </div>
            </p-form>
        </template>
        <template v-slot:modal-footer="{ ok, cancel }">
            <b-row>
                <b-col>
                    <p-button
                        variant="outline-primary"
                        :is-busy="isBusy"
                        @click="cancel"
                        >Cancel</p-button
                    >
                    <p-button
                        variant="primary"
                        @click="ok"
                        :is-busy="isBusy"
                        class="ms-2"
                        >OK</p-button
                    >
                </b-col>
            </b-row>
        </template>
    </p-modal>
</template>

<script>
import axios from 'axios';
import { downloadFileToBrowser } from '@/components/Common/BrowserDownload.js';
import pCard from '@/components/p-card.vue';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import ReportTemplates from '@/components/mixins/ReportTemplates.js';
import UserReportTemplate from '@/components/UserReportTemplate.vue';

export default {
    mixins: [ReportTemplates],    
    components: { pCard, UserReportTemplate },
    data() {
        return {
            isBusy: false,
            // Identifies which report saved templates belong to; must match the report name in Reports.vue.
            reportName: 'POS Device by Cashier(s) Reconciliation',
            disabled: true,
            reportParameters: {
                nbcUserIds: [],
                startDate: null,
                endDate: null,
                dateOption: 0,
                locationId: null,
                subtotalByPosDevice: false
            }
        };
    },
    computed: {
        pageTitle() {
            return 'POS Device by Cashier(s) Reconciliation';
        }
    },
    methods: {
        async show() {
            const nbcUsers = await selectListOptionsDataContext.getStoreDropdownData('nbcUsers');
            this.reportParameters.nbcUserIds = (nbcUsers || []).map(x => x.value);
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();
            await this.loadUserReportTemplates();
            this.$refs.cashierReconciliation.show();
        },
        // ReportTemplates hook: return false when anything in the saved template can no longer
        // be selected, and the mixin warns and loads nothing. The cashier ids are the only saved
        // values tied to changing data — the dates are literal values, not references.
        async validateReportParameters(savedParameters) {
            const nbcUsers = await selectListOptionsDataContext.getStoreDropdownData('nbcUsers') ?? [];

            return (savedParameters.nbcUserIds ?? []).every(nbcUserId =>
                nbcUsers.some(nbcUser => nbcUser.value == nbcUserId));
        },
        handleModalOk: async function() {
            if (!(await this.$refs.cashierReconciliationForm.validate())) {
                return;
            }

            if (this.reportParameters.nbcUserIds.length === 0) {
                this.$appError(
                    'Please select one or more Cashiers.'
                );
                return;
            }

            this.isBusy = true;

            // The current location always wins: a restored template carries the location that was
            // active when it was saved, which would otherwise run the report for the wrong one.
            this.reportParameters.locationId = await selectListOptionsDataContext.getSelectedLocationIdAsync();

            if (!(await this.saveUserReportTemplate())) {
                this.isBusy = false;
                return;
            }

            await axios
                .post(
                    '/sales/exportCashierReconciliation',
                    this.reportParameters,
                    {
                        responseType: 'blob'
                    }
                )
                .then(result => {
                    downloadFileToBrowser(
                        result.data,
                        'POS Device by Cashier(s) Reconciliation',
                        'application/pdf'
                    );
                    this.$appSuccess(
                            `POS Device by Cashier(s) Reconciliation report generated successfully.`
                        )
                    this.$refs.cashierReconciliation.hide();
                })
                .finally(() => {
                    this.isBusy = false;
                });
        }
    }
};
</script>

<style scoped>
.static-height {
    height:370px
}
</style>
